import React, { useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { formatFileSize } from '@/common/utils/format';
import ModalCenter from '@/common/components/modal/ModalCenter';
import { Button } from '@/common/components/button/Button';
import FileUpload from '@/common/components/fileUpload/FileUpload';
import { LoadingIcon } from '@/common/assets/icon/icon';

interface FileUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFileUpload: (file: File) => void;
}

export const FileUploadModal: React.FC<FileUploadModalProps> = ({
  isOpen,
  onClose,
  onFileUpload,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleNext = async () => {
    if (selectedFiles.length > 0) {
      setIsLoading(true);
      try {
        await onFileUpload(selectedFiles[0]);
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <ModalCenter
      isOpen={isOpen}
      onClose={onClose}
      title="사업자등록증 업로드"
      maxWidth="500px"
      footer={
        <FooterButtonGroup>
          <Button 
            $size="md"
            $variant="outline"
            onClick={onClose}
            disabled={isLoading}
            style={{ flex: 1 }}
          >
            취소
          </Button>
          <Button 
            $size="md"
            $variant="fill"
            onClick={handleNext}
            disabled={selectedFiles.length === 0 || isLoading}
            style={{ flex: 1 }}
          >
            {isLoading ? (
              <LoadingContent>
                <LoadingSpinner />
                업로드 중...
              </LoadingContent>
            ) : '다음'}
          </Button>
        </FooterButtonGroup>
      }
    >
      <ContentWrapper>
        
        <Description>
          최근 사업자등록증 파일을 업로드해주세요.
        </Description>
        
        <FileUploadContainer>
          <FileUpload
            type="lg"
            accept=".pdf"
            maxSize={10 * 1024 * 1024} // 10MB
            files={selectedFiles}
            onChange={setSelectedFiles}
            label="여기에 파일을 끌어다 놓거나 클릭하세요."
          />
        </FileUploadContainer>
        
        <InfoList>
          <InfoItem>PDF 파일만 업로드 가능합니다</InfoItem>
          <InfoItem>최대 파일 크기: 10MB</InfoItem>
        </InfoList>
      </ContentWrapper>
    </ModalCenter>
  );
};

const ContentWrapper = styled.div`
  padding: 1.5rem;
  text-align: center;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
`;

const Description = styled.p`
  margin: 0 0 24px;
  font-size: 14px;
  line-height: 1.5;
  color: #666;
`;

const FileUploadContainer = styled.div`
  margin-bottom: 20px;
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
  margin-bottom: 8px;
`;

const InfoItem = styled.div`
  font-size: 12px;
  color: #666;
  display: flex;
  align-items: center;
  
  &::before {
    content: '* ';
    margin-right: 8px;
  }
`;

const FooterButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;

const LoadingContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: ${spin} 1s linear infinite;
`; 